import { format } from "date-fns";
import { Post } from "../models/Post";

/** One booked slot on a given day: a post plus the connections going out at that time. */
export interface ScheduledSlot {
  postId: string;
  label: string;
  /** Epoch ms. */
  time: number;
  connectionIds: string[];
  /** True once every connection in this slot has published. */
  published: boolean;
}

export type ScheduleByDate = Record<string, ScheduledSlot[]>;

/** Calendar day key in local time. Matches the Date objects react-day-picker hands back. */
export function dayKey(date: Date | number): string {
  return format(typeof date === "number" ? new Date(date) : date, "yyyy-MM-dd");
}

const toMilliseconds = (timestamp: number): number =>
  timestamp.toString().length > 10 ? timestamp : timestamp * 1000;

const postLabel = (post: Post): string =>
  post.description?.trim() ||
  post.title?.trim() ||
  `${post.mediaItems?.[0]?.type === "video" ? "Video" : "Image"} post`;

/**
 * Buckets posts by the local day they go out on, so the schedule picker can show
 * what a day already holds. Mirrors the time-resolution rule used everywhere else:
 * a connection's own time wins, else the post's, else the time it actually published.
 */
export function buildScheduleByDate(
  posts: Post[],
  options: { excludePostId?: string } = {}
): ScheduleByDate {
  const { excludePostId } = options;
  const byDate: ScheduleByDate = {};

  posts.forEach((post) => {
    if (excludePostId && post.id === excludePostId) return;

    const postScheduledFor =
      post.scheduledFor && post.scheduledFor > 0 ? post.scheduledFor : undefined;

    // Connections that share an exact timestamp go out together, so they read as one slot.
    const slotConnections = new Map<number, string[]>();
    const slotPublished = new Map<number, boolean>();

    Object.entries(post.connections).forEach(([connectionId, conn]) => {
      const connScheduledFor =
        conn.scheduledFor && conn.scheduledFor > 0 ? conn.scheduledFor : undefined;
      const isPublished = conn.status === "published";
      const time =
        connScheduledFor ??
        postScheduledFor ??
        (isPublished && conn.publishedAt ? conn.publishedAt : undefined);
      if (!time) return;

      const ms = toMilliseconds(time);
      slotConnections.set(ms, [...(slotConnections.get(ms) || []), connectionId]);
      slotPublished.set(ms, (slotPublished.get(ms) ?? true) && isPublished);
    });

    slotConnections.forEach((connectionIds, time) => {
      const key = dayKey(time);
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push({
        postId: post.id,
        label: postLabel(post),
        time,
        connectionIds,
        published: slotPublished.get(time) ?? false,
      });
    });
  });

  Object.values(byDate).forEach((slots) => slots.sort((a, b) => a.time - b.time));

  return byDate;
}

/** What a calendar day is carrying, for the dot under its number. */
export interface DayLoad {
  /** Everything on the day, published or not. */
  count: number;
  /** Still to go out: unpublished AND in the future. */
  upcoming: number;
  /** Nothing on this day can still happen — it's a record, not a plan. */
  isHistory: boolean;
}

/**
 * Splits a day's slots into what's still coming and what's already behind us.
 *
 * "Unpublished" alone does not mean upcoming. A post that failed, or never fired, or only
 * half went out, stays unpublished forever — so keying off publish status alone paints
 * last May's failures the same colour as next week's plans.
 */
export function dayLoad(slots: ScheduledSlot[], now: number): DayLoad {
  const upcoming = slots.filter(
    (slot) => !slot.published && slot.time > now
  ).length;
  return {
    count: slots.length,
    upcoming,
    isHistory: slots.length > 0 && upcoming === 0,
  };
}

/** How close two posts can sit before we warn. Mirrors CONFLICT_BUFFER_MS in SmartScheduler.ts. */
export const CONFLICT_BUFFER_MS = 2 * 60 * 60 * 1000;

/** Unpublished slots within the conflict buffer of `time`. */
export function findConflicts(
  byDate: ScheduleByDate,
  time: number
): ScheduledSlot[] {
  // A conflict can sit on the adjacent day when `time` is near midnight.
  const day = 24 * 60 * 60 * 1000;
  return [dayKey(time - day), dayKey(time), dayKey(time + day)]
    .filter((key, idx, keys) => keys.indexOf(key) === idx)
    .flatMap((key) => byDate[key] || [])
    .filter(
      (slot) => !slot.published && Math.abs(slot.time - time) < CONFLICT_BUFFER_MS
    )
    .sort((a, b) => a.time - b.time);
}
